import React, { useState, useCallback } from "react";
import { Card, Button, Table, Space, message, Badge, Dropdown, Tag, Tooltip, Divider, Spin, Modal } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownOutlined, SettingOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { useLoadList, useDelete } from "../../../hooks";
import { useTableSelection } from "../../../hooks";
import { contractManagementService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import ProductBundleFilter from "./components/ProductBundleFilter";
import ProductBundleForm from "./components/ProductBundleForm";
import ProductBundleDetailDrawer from "./components/ProductBundleDetailDrawer";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { SERVICE_TYPE_MAP } from "../SupplierContract/constants";

const { confirm } = Modal;

const ProductBundleList = () => {
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({
        is_active: true, // 默认只显示启用的产品套装
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20, // 与后端默认值保持一致
    });
    const [formVisible, setFormVisible] = useState(false);
    const [currentBundle, setCurrentBundle] = useState(null);
    const [formMode, setFormMode] = useState("add"); // 'add' 或 'edit'
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("productBundles", contractManagementService.productBundle.getProductBundleList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection, handleRowSelectionChange } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
        enableGlobalTracking: true,
        mouseMoveThrottle: 30,
    });

    // 删除产品套装的Hook
    const deleteMutation = useDelete("productBundles", contractManagementService.productBundle.deleteProductBundle, {
        onSuccess: () => {
            message.success("删除成功");
            refetch();
        },
    });

    // 服务类型映射（复用 settlement STANDARD_COST_TYPE）
    const serviceTypeMap = SERVICE_TYPE_MAP;

    // 全选当前页面所有产品套装
    const handleSelectAllVisible = useCallback(() => {
        const visibleKeys = (data?.items || []).map((item) => item.id);
        handleRowSelectionChange(visibleKeys, data?.items || []);
    }, [data?.items, handleRowSelectionChange]);

    // 自定义行属性函数 - 只允许序号列选中
    const getCustomRowProps = useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);
            const { onClick, ...restProps } = originalProps;
            return {
                ...restProps,
                // 不添加 onClick 事件，避免整行点击选中
            };
        },
        [getRowProps],
    );

    // 查看产品套装详情
    const viewBundleDetail = useCallback((bundle) => {
        console.log("🔍 查看产品套装详情被调用, bundle:", bundle);
        console.log("🔍 产品套装ID:", bundle?.id, "产品套装名称:", bundle?.name);
        setSelectedBundle(bundle);
        setDetailDrawerVisible(true);
    }, []);

    // 查看产品套装项 - 已合并到详情抽屉中
    const handleViewBundleItems = useCallback((record) => {
        setSelectedBundle(record);
        setDetailDrawerVisible(true);
    }, []);

    // 处理表格分页变化
    const handleTableChange = useCallback((newPagination, filters, sorter) => {
        setPagination(newPagination);
    }, []);

    // 处理搜索
    const handleSearch = useCallback(
        (values) => {
            const params = {};

            if (values.name) {
                params.name = values.name;
            }

            if (values.service_type) {
                params.service_type = values.service_type;
            }

            if (values.is_active !== undefined) {
                params.is_active = values.is_active;
            }

            setFilterParams(params);
            setPagination({ ...pagination, current: 1 });
        },
        [pagination],
    );

    // 处理重置
    const handleReset = useCallback(() => {
        setFilterParams({ is_active: true });
        setPagination({ ...pagination, current: 1 });
    }, [pagination]);

    // 处理新增
    const handleAdd = useCallback(() => {
        setCurrentBundle(null);
        setFormMode("add");
        setFormVisible(true);
    }, []);

    // 处理编辑
    const handleEdit = useCallback((record) => {
        console.log("🔧 ProductBundleList - handleEdit 被调用，数据:", record);
        setCurrentBundle(record);
        setFormMode("edit");
        setFormVisible(true);
        console.log("🔧 ProductBundleList - 设置状态完成，currentBundle:", record, "formMode: edit, formVisible: true");
    }, []);

    // 处理删除
    const handleDelete = useCallback(
        (record) => {
            confirm({
                title: "确认删除",
                icon: <ExclamationCircleOutlined />,
                content: `确定要删除产品套装 "${record.name}" 吗？删除后相关的产品套装项也将被删除。`,
                okText: "确定",
                okType: "danger",
                cancelText: "取消",
                onOk() {
                    deleteMutation.mutate(record.id);
                },
            });
        },
        [deleteMutation],
    );

    // 处理批量删除
    const handleBatchDelete = useCallback(() => {
        if (selectedRowKeys.length === 0) {
            message.warning("请选择要删除的产品套装");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 个产品套装吗？删除后相关的产品套装项也将被删除。`,
            okText: "确定",
            okType: "danger",
            cancelText: "取消",
            onOk: async () => {
                try {
                    await Promise.all(selectedRowKeys.map((id) => contractManagementService.productBundle.deleteProductBundle(id)));
                    message.success("批量删除成功");
                    refetch();
                    clearSelection();
                } catch (error) {
                    message.error("批量删除失败");
                    console.error("批量删除失败:", error);
                }
            },
        });
    }, [selectedRowKeys, clearSelection, refetch]);

    // 处理表单成功
    const handleFormSuccess = useCallback(() => {
        setFormVisible(false);
        setCurrentBundle(null);
        refetch();
    }, [refetch]);

    // 处理产品套装项管理成功 - 现在由详情抽屉处理
    const handleBundleItemSuccess = useCallback(() => {
        refetch(); // 刷新列表以更新产品套装项数量
    }, [refetch]);

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // 权限检查
    if (!hasPermission("contract:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看产品套装的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"} | 权限列表: {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "产品套装名称",
            dataIndex: "name",
            key: "name",
            width: 200,
            ellipsis: true,
            render: (text, record) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        viewBundleDetail(record);
                    }}
                >
                    {text || "未命名产品套装"}
                </a>
            ),
        },
        {
            title: "服务类型",
            dataIndex: "service_type",
            key: "service_type",
            width: 120,
            render: (type) => {
                const typeInfo = serviceTypeMap[type] || { text: type, color: "default" };
                return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
            },
        },
        {
            title: "产品套装描述",
            dataIndex: "description",
            key: "description",
            width: 250,
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span>{text || "-"}</span>
                </Tooltip>
            ),
        },
        {
            title: "适用机场",
            dataIndex: "applicable_airports",
            key: "applicable_airports",
            width: 150,
            render: (airports) => {
                if (!airports || airports.length === 0) {
                    return <Tag color="default">全部</Tag>;
                }
                return (
                    <Space size={4} wrap>
                        {airports.slice(0, 2).map((airport, index) => (
                            <Tag key={index} color="blue" size="small">
                                {airport}
                            </Tag>
                        ))}
                        {airports.length > 2 && (
                            <Tooltip title={airports.join(", ")}>
                                <Tag color="default" size="small">
                                    +{airports.length - 2}
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (isActive) => <Badge className="status-badge" status={isActive ? "success" : "error"} text={isActive ? "启用" : "停用"} />,
        },
        {
            title: "产品套装项数量",
            key: "bundle_items_count",
            width: 120,
            render: (_, record) => (
                <Button type="link" size="small" onClick={() => handleViewBundleItems(record)}>
                    {record.bundle_items_count || 0} 项
                </Button>
            ),
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 180,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="contract:read">
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                viewBundleDetail(record);
                            }}
                        >
                            详情
                        </Button>
                    </PermissionGuard>

                    <PermissionGuard permission="contract:update">
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "edit",
                                        label: "编辑产品套装",
                                        icon: <EditOutlined />,
                                        onClick: () => handleEdit(record),
                                    },
                                    {
                                        key: "bundle-items",
                                        label: "管理产品套装项",
                                        icon: <SettingOutlined />,
                                        onClick: () => handleViewBundleItems(record),
                                    },
                                    {
                                        type: "divider",
                                    },
                                    {
                                        key: "delete",
                                        label: "删除产品套装",
                                        icon: <DeleteOutlined />,
                                        danger: true,
                                        onClick: () => handleDelete(record),
                                        disabled: !hasPermission("contract:delete"),
                                    },
                                ],
                            }}
                            placement="bottomRight"
                            trigger={["click"]}
                        >
                            <Button type="link" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                        </Dropdown>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            {/* 页面标题区域 - 参考产品页面布局 */}
            <div className="page-header-compact">
                <div className="page-title-compact">产品套装管理</div>
                <div className="page-action-compact">
                    <PermissionGuard permission="contract:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            新增产品套装
                        </Button>
                    </PermissionGuard>
                </div>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <ProductBundleFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 产品套装列表 */}
            <Card className="data-card">
                <div style={{ position: "relative" }}>
                    {/* 批量操作工具栏 - 参考产品的实现 */}
                    {selectedRowKeys.length > 0 && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                zIndex: 10,
                                padding: "6px 12px",
                                background: "linear-gradient(90deg, rgba(245, 245, 245, 1) 0%, rgba(245, 245, 245,1) 56%, rgba(245, 245, 245, 0) 60%)",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                pointerEvents: "none", // 确保右侧透明区域不阻挡鼠标操作
                            }}
                        >
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", width: "60%", pointerEvents: "auto" }}>
                                <span style={{ marginRight: 8, fontSize: "12px" }}>
                                    已选择 <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 个产品套装
                                </span>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <Button size="small" type="link" onClick={handleSelectAllVisible} style={{ padding: "0 4px" }}>
                                    全选当前页
                                </Button>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <Button size="small" type="link" onClick={clearSelection} style={{ padding: "0 4px" }}>
                                    取消选择
                                </Button>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <PermissionGuard permission="contract:delete">
                                    <Button size="small" type="primary" danger ghost icon={<DeleteOutlined />} onClick={handleBatchDelete} style={{ fontSize: "12px" }}>
                                        批量删除
                                    </Button>
                                </PermissionGuard>
                            </div>
                        </div>
                    )}

                    {/* 表格 */}
                    <Table
                        ref={tableRef}
                        size="small"
                        columns={columns}
                        dataSource={data?.items || []}
                        loading={isLoading}
                        rowKey="id"
                        rowSelection={
                            hasPermission("contract:delete")
                                ? {
                                      ...rowSelection,
                                      columnWidth: 6,
                                  }
                                : null
                        }
                        pagination={{
                            ...pagination,
                            total: data?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条记录`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1400 }}
                        onRow={getCustomRowProps}
                    />
                </div>
            </Card>

            {/* 表单抽屉 */}
            <ProductBundleForm
                visible={formVisible}
                mode={formMode}
                initialValues={currentBundle}
                onSuccess={handleFormSuccess}
                onCancel={() => {
                    setFormVisible(false);
                    setCurrentBundle(null);
                }}
                useDrawer={true}
                width={800}
            />

            {/* 详情抽屉 - 包含产品套装项管理功能 */}
            <ProductBundleDetailDrawer visible={detailDrawerVisible} onClose={() => setDetailDrawerVisible(false)} bundle={selectedBundle} onEdit={handleEdit} onSuccess={handleBundleItemSuccess} />
        </div>
    );
};

export default ProductBundleList;
